import { useEffect, useRef } from "react";
import { useGetSet, useKeyPress } from "react-use";
import Konva from "konva";
import { Circle } from "react-konva";
import consts from "shared/consts";
import { GhostCanvasElement } from "../../ghost-canvas-element";
import { viewportToStage, Point } from "frontend/utils/math-utils";
import { lenSqr } from "frontend/utils/point-utils";
import { isMac } from "frontend/utils/keyboard-shortcuts";
import { combineColorOpacity, parseColor } from "frontend/utils/color-utils";
import { snapConnectorRotation } from "./connector-attach-utils";
import { ConnectorEndpoint } from "./connector-utils";
import * as connectorConsts from "./connector-constants";
import * as attachUtils from "./connector-attach-utils";
import { Degrees } from "frontend/utils/transform";

export type NewConnectorData = {
  lineType: "curve" | "line" | "elbow";
  startArrow: string;
  endArrow: string;
  dash: number;
  strokeWidth: number;
  stroke: string;
};

type AnchorPoint = {
  side: string;
  position: ConnectorEndpoint;
};

type ConnectorAnchor = { pos: ConnectorEndpoint; shapeId?: string; side?: string; t?: number; rotation?: Degrees };

type HighlightedAnchor = null | ConnectorAnchor;

type GhostConnector = null | {
  start: ConnectorAnchor;
  end: ConnectorAnchor;
};

// The distance is measured in the canvas coordinate system.
// Maybe we should measure it in screen pixels instead
const MinimumLengthOfConnector = 10;

export default function ConnectorBuilder({
  stage,
  layer,
  initialState,
  mouseDownTime,
  newElementData,
  onCreate,
}: {
  stage: Konva.Stage | null;
  layer: Konva.Layer | null;
  initialState: ConnectorAnchor | null;
  mouseDownTime: number;
  newElementData: NewConnectorData;
  onCreate: (newElementData: NewConnectorData, ghost: any) => void;
}) {
  // State - useGetSet because we access all of them from event handlers
  // and we don't want stale variables
  const [getGhost, setGhost] = useGetSet<GhostConnector>(() => {
    return initialState ? { start: initialState, end: { pos: initialState.pos } } : null;
  });
  const [getAnchorPoints, setAnchorPoints] = useGetSet<AnchorPoint[] | null>(null); // in viewport coordinates
  const [getHighlightedAnchor, setHighlightedAnchor] = useGetSet<HighlightedAnchor>(null); // in viewport coordinates
  const [getConnectorStartTime, setConnectorStartTime] = useGetSet<number>(mouseDownTime);

  const ctrlOrCmd = useKeyPress(isMac() ? "Meta" : "Control")[0];
  const shift = useKeyPress("Shift")[0];
  const modifierKeys = useRef({ ctrlOrCmd, shift });
  modifierKeys.current = { ctrlOrCmd, shift };

  const freshElementData = useRef(newElementData);
  freshElementData.current = newElementData;

  const layerTransform = layer?.getAbsoluteTransform();
  const toStage = viewportToStage(layerTransform as Konva.Transform);
  const mousePosToStage = (e: MouseEvent) => toStage({ x: e.offsetX, y: e.offsetY });

  function finishAndCreate(ghost: GhostConnector, anchor: HighlightedAnchor) {
    if (!ghost) {
      return;
    }
    if (lenSqr(ghost.start.pos, ghost.end.pos) < MinimumLengthOfConnector / (stage?.scaleX() ?? 1)) {
      return;
    }
    if (Date.now() - getConnectorStartTime() < 100) {
      return;
    }
    if (modifierKeys.current.ctrlOrCmd) {
      ghost.end.shapeId = undefined;
      ghost.end.side = undefined;
    } else if (anchor && (anchor.shapeId != ghost.start.shapeId || anchor.side != ghost.start.side)) {
      ghost.end = { pos: toStage(anchor.pos), shapeId: anchor.shapeId, side: anchor.side, t: anchor.t };
    }
    onCreate(freshElementData.current as any, ghost);
    setGhost(null);
    setHighlightedAnchor(null);
  }

  useEffect(() => {
    const container = stage?.container();
    container?.addEventListener("mousemove", onMouseMove);
    container?.addEventListener("mousedown", onMouseDown);
    container?.addEventListener("mouseup", onMouseUp);
    return () => {
      const container = stage?.container();
      container?.removeEventListener("mousemove", onMouseMove);
      container?.removeEventListener("mousedown", onMouseDown);
      container?.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  function updateHighlightedNode(
    mousePosInCanvasElement: Point,
    mousePosStage: Point,
    stage: Konva.Stage | null,
    elementsLayer: Konva.Layer | null
  ) {
    const snap = attachUtils.checkSnapping(mousePosInCanvasElement, mousePosStage, stage, elementsLayer);

    if (snap == null) {
      setHighlightedAnchor(null);
      setAnchorPoints(null);
      return;
    }

    if (snap instanceof attachUtils.SnapToAnchor) {
      setAnchorPoints(Object.entries(snap.anchors).map(([side, position]) => ({ side, position })));
      setHighlightedAnchor({ pos: snap.position, rotation: snap.rotation, shapeId: snap.node.id(), side: snap.side });
      return;
    }

    if (snap instanceof attachUtils.SnapToOutline) {
      setAnchorPoints(null);
      const highlight = {
        pos: snap.xy,
        rotation: snap.rotation,
        shapeId: snap.node.attrs.id,
        side: "outline",
        t: snap.t,
      };
      setHighlightedAnchor(highlight);
      return;
    }

    if (snap instanceof attachUtils.SnapJustShowAnchors) {
      setAnchorPoints(Object.entries(snap.anchors).map(([side, position]) => ({ side, position })));
      setHighlightedAnchor(null);
    }
  }

  function onMouseMove(e: MouseEvent) {
    const pos = mousePosToStage(e);
    const ghost = getGhost();
    updateHighlightedNode(e, pos, stage, layer);
    if (ghost) {
      // update ghost end point to stage / socket
      const anchor = getHighlightedAnchor();
      setGhost((ghost: any) => {
        if (ghost) {
          if (
            anchor &&
            anchor.side != "outline" &&
            modifierKeys.current.ctrlOrCmd == false &&
            (anchor.shapeId != ghost.start.shapeId || anchor.side != ghost.start.side)
          ) {
            const end = {
              pos: { ...(anchor.pos as Point), rotation: anchor.pos.rotation },
              shapeId: anchor.shapeId,
              side: anchor.side,
            };
            return { ...ghost, end };
          } else if (anchor && anchor.side == "outline" && modifierKeys.current.ctrlOrCmd == false) {
            return {
              ...ghost,
              end: {
                pos: { ...anchor.pos, rotation: anchor.rotation },
                shapeId: anchor.shapeId,
                side: anchor.side,
                t: anchor.t,
              },
            };
          } else {
            if (modifierKeys.current.shift) {
              const pos2 = snapConnectorRotation(pos, ghost.start.pos);
              return { ...ghost, end: { pos: pos2 } };
            }
            return { ...ghost, end: { pos: pos } };
          }
        }
        return null;
      });
    }
  }

  function onMouseDown(e: MouseEvent) {
    const pos = mousePosToStage(e);
    const ghost = getGhost();
    const anchor = getHighlightedAnchor();
    if (!ghost) {
      setConnectorStartTime(Date.now());
      let start;
      if (anchor && !modifierKeys.current.ctrlOrCmd) {
        const pos = { ...(anchor.pos as Point), rotation: (anchor.rotation || 0) as Degrees };
        start = { pos, shapeId: anchor.shapeId, side: anchor.side, t: anchor.t, rotation: pos.rotation };
      } else {
        start = { pos };
      }
      setGhost({ start, end: { pos } });
    } else {
      finishAndCreate(ghost, anchor);
    }
  }

  function onMouseUp() {
    const ghost = getGhost();
    if (ghost) {
      finishAndCreate(ghost, getHighlightedAnchor());
    }
  }

  function renderAnchorPoints(scale: number) {
    const points = getAnchorPoints();
    if (!points || points.length == 0) return null;
    return (
      <>
        {points.map(({ position }, index) => (
          <Circle
            key={index}
            listening={false}
            x={position.x}
            y={position.y}
            radius={connectorConsts.ConnectorSnapPointRadius / scale}
            fill="white"
            stroke="#1973FF"
            strokeWidth={2 / scale}
          />
        ))}
      </>
    );
  }
  function renderHighlightedAnchor(scale: number) {
    const anchor = getHighlightedAnchor();
    if (!anchor) return null;
    // don't allow connection to same point, unless it's the outline
    if (ghost && anchor.shapeId == ghost.start.shapeId && anchor.side == ghost.start.side && anchor.side != "outline")
      return null;
    return (
      <Circle
        listening={false}
        x={anchor.pos.x}
        y={anchor.pos.y}
        radius={connectorConsts.ConnectorSnapPointRadius / scale}
        fill="#1973FF"
        stroke="white"
        strokeWidth={2 / scale}
      />
    );
  }

  const ghost = getGhost();
  const stageScale = stage?.scaleX() ?? 1;
  const { color, opacity } = parseColor(newElementData.stroke);
  const newOpacity = (opacity / 255) * 0.33 * 255;
  const newcolor = combineColorOpacity(color, newOpacity);

  return (
    <>
      {ghost && (
        <GhostCanvasElement
          elementType={consts.CANVAS_ELEMENTS.CONNECTOR}
          point1={ghost.start.pos}
          point2={ghost.end.pos}
          data={{
            connector: {
              ...newElementData,
              start: ghost.start,
              end: ghost.end,
              stroke: newcolor,
            },
          }}
        />
      )}
      {!ctrlOrCmd && stageScale > 0.1 && renderAnchorPoints(stageScale)}
      {!ctrlOrCmd && stageScale > 0.1 && renderHighlightedAnchor(stageScale)}
    </>
  );
}
